import React, { MouseEvent } from 'react';
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

type BooleanFilterOption = 'all' | 'with' | 'without';

interface BooleanFilterToggleProps {
  label: string;
  allLabel: string;
  withLabel: string;
  withoutLabel: string;
  value: boolean | undefined;
  onChange: (value: boolean | undefined) => void;
}

const toggleStyles = {
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 40,
  p: 0.375,
  gap: 0.5,
  border: '1px solid',
  borderColor: 'softBorder',
  borderRadius: 2,
  bgcolor: 'sunken',
  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.14)',
  '& .MuiToggleButtonGroup-grouped': {
    flex: '1 1 0',
    minWidth: 0,
    minHeight: 30,
    px: 1.25,
    border: '1px solid transparent !important',
    borderRadius: '4px !important',
    textTransform: 'none',
  },
  '& .MuiToggleButtonGroup-grouped.Mui-selected': {
    color: 'primary.contrastText',
    bgcolor: 'primary.main',
    '&:hover': { bgcolor: 'primary.dark' },
  },
};

const getSelectedOption = (value: boolean | undefined): BooleanFilterOption => {
  if (value == null) {
    return 'all';
  }

  return value ? 'with' : 'without';
};

const BooleanFilterToggle = ({
  label,
  allLabel,
  withLabel,
  withoutLabel,
  value,
  onChange,
}: BooleanFilterToggleProps) => {
  const handleChange = (
    event: MouseEvent<HTMLElement>,
    option: BooleanFilterOption | null
  ) => {
    if (option === null || option === 'all') {
      onChange(undefined);
      return;
    }

    onChange(option === 'with');
  };

  return (
    <Stack>
      <Typography fontSize={12} lineHeight={1.5} pb="2px">
        {label}
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={getSelectedOption(value)}
        onChange={handleChange}
        aria-label={label}
        sx={toggleStyles}
      >
        <ToggleButton value="all" aria-label={allLabel}>
          {allLabel}
        </ToggleButton>
        <ToggleButton value="with" aria-label={withLabel}>
          {withLabel}
        </ToggleButton>
        <ToggleButton value="without" aria-label={withoutLabel}>
          {withoutLabel}
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};

export { BooleanFilterToggle };
